import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { includes } from 'lodash';
import { configurations as i18nNamespace } from '../../../../i18n/namespace';
import { history } from 'store';
import { isAllowed } from 'utils/permissions';
import ConfigurationInfoCard from './ConfigurationInfoCard';
import styles from '../../style.module.scss';
import { ReactComponent as InternetAccessIcon } from '../../assets/icon-internet-access-2color.svg';
import { ReactComponent as SyslogIcon } from '../../assets/icon-syslog-2color.svg';
import { ReactComponent as SiemIcon } from '../../assets/icon-siem-2color.svg';
import { ReactComponent as NetworkAliasesIcon } from '../../assets/icon-network-aliases-2color.svg';
import ProxyCardStatus from './ProxyCardStatus';

export { default as AutoSupportCard } from './AutoSupportCard';
export { default as SecurityCertificateCard } from './SecurityCertificateCard';
export { default as ProxyConfiguration } from './proxyConfiguration';

// Unreleased system configuration cards stay behind this shared flag until
// their individual features land.
export const SYSTEM_CONFIGURATION_FEATURE = 'NetApp.Console/system-configuration';
export const AUTOSUPPORT_FEATURE = 'NetApp.Console/autosupport';

const createCard = (
    displayName,
    { titleKey, descriptionKey, descriptionClassName, Icon, routePath, showStatus: ShowStatus }
) => {
    const Card = () => {
        const { t } = useTranslation(i18nNamespace);
        const { selectedOrg: { rules } = {} } = useSelector(state => state.tenancy);
        const [isConfigured, setIsConfigured] = useState(false);
        const [proxyConfig, setProxyConfig] = useState(null);
        const [loadError, setLoadError] = useState(null);
        // Cards with a status component only know what is configured once that
        // component has loaded it, so the action waits for the first report.
        const [isStatusLoading, setIsStatusLoading] = useState(Boolean(ShowStatus));
        const canViewCard = isAllowed(rules, 'notifications', 'access');
        const canConfigure = includes(
            rules,
            isConfigured ? 'bxp:notifications-modify' : 'bxp:notifications-create'
        );

        const onConfiguredChange = (configured, config, error) => {
            setIsConfigured(configured);
            setProxyConfig(config || null);
            setLoadError(error || null);
            setIsStatusLoading(false);
        };

        return (
            <ConfigurationInfoCard
                title={t(titleKey)}
                // The status renders its own loader, so the description is held
                // back until the load settles to keep that area visible.
                description={isConfigured || loadError || isStatusLoading ? undefined : t(descriptionKey)}
                descriptionClassName={descriptionClassName}
                showStatus={ShowStatus ? <ShowStatus onConfiguredChange={onConfiguredChange} /> : null}
                iconRenderer={() => <Icon />}
                actionLabel={t(isConfigured ? 'edit' : 'configure')}
                onAction={() => history.push(routePath, { proxyConfig })}
                isActionDisabled={isStatusLoading}
                hasPermission={canConfigure}
                hasNoPermission={!canViewCard}
                hasError={Boolean(loadError)}
                errorMessage={loadError}
            />
        );
    };
    Card.displayName = displayName;

    return React.memo(Card);
};

export const ProxyCard = createCard('ProxyCard', {
    titleKey: 'proxy',
    descriptionKey: 'proxy_card_desc',
    descriptionClassName: styles.multiLineDescription,
    Icon: InternetAccessIcon,
    routePath: '/console-configurations/proxy',
    showStatus: ProxyCardStatus,
});

export const SyslogCard = createCard('SyslogCard', {
    titleKey: 'syslog',
    descriptionKey: 'syslog_desc',
    Icon: SyslogIcon,
    routePath: '/console-configurations/syslog',
});

export const SiemIntegrationCard = createCard('SiemIntegrationCard', {
    titleKey: 'siem_integration',
    descriptionKey: 'siem_integration_desc',
    Icon: SiemIcon,
    routePath: '/console-configurations/siem',
});

export const NetworkAliasesCard = createCard('NetworkAliasesCard', {
    titleKey: 'network_aliases',
    descriptionKey: 'network_aliases_desc',
    Icon: NetworkAliasesIcon,
    routePath: '/console-configurations/network-aliases',
});
